import type { FunctionComponent } from "react";
import {
  PostStatus,
  type IPost,
} from "../../../../types/models/post.models.types";
import { Button } from "../../../../components/ui/button";
import { usePostsApproveDraftAdminHttp } from "../../../../hooks/http/admin/posts/posts-approve-draft.admin.http";
import { usePostsApprovePostAdminHttp } from "../../../../hooks/http/admin/posts/posts-approve-post.admin.http";
import usePostStore from "../../../../store/post.store";
import { handleHttpError, useHandleNavigate } from "../../../../utils/utils";
import { useLocation, useNavigate } from "react-router-dom";
import { useUnflagPostAdminHttp } from "../../../../hooks/http/admin/posts/post-unflag.admin.http";
import { useToast } from "../../../../hooks/use-toast";

type AppProps = {
  post: IPost;
};

export const CampaignPostActionAdminPartial: FunctionComponent<AppProps> = ({
  post,
}) => {
  const { execute: approveDraftHttp, fetching: fetchingApproveDraft } =
    usePostsApproveDraftAdminHttp();

  const { execute: approvePostHttp, fetching: fetchingApprovePost } =
    usePostsApprovePostAdminHttp();
  const { execute: unFlagPostHttp, fetching: unflaggingPost } =
    useUnflagPostAdminHttp();
  const { removeById } = usePostStore();
  const location = useLocation();
  const { handleNavigate } = useHandleNavigate(
    location.pathname.replace(`/${post.id}`, "")
  );
  const navigate = useNavigate();
  const { toast } = useToast();

  async function approveDraft() {
    const response = await approveDraftHttp({
      body: null,
      pathParams: { id: post.id },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    removeById(post.id);
    handleNavigate();
  }

  async function approvePost() {
    const response = await approvePostHttp({
      body: null,
      pathParams: { id: post.id },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    removeById(post.id);
    handleNavigate();
  }

  function rejectDraft() {
    if (location.pathname.endsWith(post.id)) {
      return navigate(`${location.pathname}/draft-reject`);
    }

    navigate(`${location.pathname}/${post.id}/draft-reject`);
  }

  function rejectPost() {
    if (location.pathname.endsWith(post.id)) {
      return navigate(`${location.pathname}/post-reject`);
    }
    navigate(`${location.pathname}/${post.id}/post-reject`);
  }

  function disqualifyPost() {
    if (location.pathname.endsWith(post.id)) {
      return navigate(`${location.pathname}/disqualify`);
    }
    navigate(`${location.pathname}/${post.id}/disqualify`);
  }

  function flagPost() {
    if (location.pathname.endsWith(post.id)) {
      return navigate(`${location.pathname}/flag`);
    }
    navigate(`${location.pathname}/${post.id}/flag`);
  }

  async function unFlagPost() {
    const response = await unFlagPostHttp({
      pathParams: { id: post.id },
      body: null,
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    removeById(post.id);
  }

  function getActionMetadata(post: IPost) {
    switch (post.postStatus.status) {
      case PostStatus.draft:
        return {
          button1: {
            action: approveDraft,
            fetching: fetchingApproveDraft,
            title: "Approve",
            className:
              "bg-approvebuttonbg border-approvebuttonborder text-approvebuttontext",
          },
          button2: {
            action: rejectDraft,
            fetching: false,
            title: "Reject",
            className:
              "bg-rejectbuttonbg border-rejectbuttonborder text-rejectbuttontext",
          },
        };
      case PostStatus.draftApproved:
        return {
          button1: {
            action: rejectDraft,
            fetching: false,
            title: "Change to rejected",
            className:
              "bg-rejectbuttonbg border-rejectbuttonborder text-rejectbuttontext",
          },
        };
      case PostStatus.draftRejected:
        return {
          button1: {
            action: approveDraft,
            fetching: fetchingApproveDraft,
            title: "Change to approved",
            className:
              "bg-approvebuttonbg border-approvebuttonborder text-approvebuttontext",
          },
        };
      case PostStatus.post:
        return {
          button1: {
            action: approvePost,
            fetching: fetchingApprovePost,
            title: "Approve",
            className:
              "bg-approvebuttonbg border-approvebuttonborder text-approvebuttontext",
          },
          button2: {
            action: rejectPost,
            fetching: false,
            title: "Reject",
            className:
              "bg-rejectbuttonbg border-rejectbuttonborder text-rejectbuttontext",
          },
        };
      case PostStatus.postApproved:
        return {
          button1: {
            action: flagPost,
            fetching: false,
            title: "Flag",
            className:
              "bg-tertiarybuttonbg border-tertiarybuttonborder text-tertiarybuttontext",
          },
          button2: {
            action: disqualifyPost,
            fetching: false,
            title: "Disqualify",
            className:
              "bg-rejectbuttonbg border-rejectbuttonborder text-rejectbuttontext",
          },
        };
      case PostStatus.postRejected:
        return {
          button1: {
            action: approvePost,
            fetching: fetchingApprovePost,
            title: "Change to approved",
            className:
              "bg-approvebuttonbg border-approvebuttonborder text-approvebuttontext",
          },
        };
      case PostStatus.disqualify:
        return {
          button1: {
            action: approvePost,
            fetching: fetchingApprovePost,
            title: "Change to approved",
            className:
              "bg-approvebuttonbg border-approvebuttonborder text-approvebuttontext",
          },
        };
      case PostStatus.flagged:
        return {
          button1: {
            action: unFlagPost,
            fetching: unflaggingPost,
            title: "Unflag",
            className:
              "bg-tertiarybuttonbg border-tertiarybuttonborder text-tertiarybuttontext",
          },
          button2: {
            action: disqualifyPost,
            fetching: false,
            title: "Disqualify",
            className:
              "bg-rejectbuttonbg border-rejectbuttonborder text-rejectbuttontext",
          },
        };
      case PostStatus.flaggedReview:
        return {
          button1: {
            action: unFlagPost,
            fetching: unflaggingPost,
            title: "Unflag",
            className:
              "bg-tertiarybuttonbg border-tertiarybuttonborder text-tertiarybuttontext",
          },
          button2: {
            action: disqualifyPost,
            fetching: false,
            title: "Disqualify",
            className:
              "bg-rejectbuttonbg border-rejectbuttonborder text-rejectbuttontext",
          },
        };
      default:
        throw new Error("unknown post status");
    }
  }


  if(post.postStatus.status === PostStatus.vendorDraft){
	return null;
  }
  const actionMetadata = getActionMetadata(post);

  return (
    <div className="flex justify-between items-center gap-5 w-full">
      <Button
        disabled={actionMetadata.button1.fetching}
        onClick={() => {
          actionMetadata.button1.action();
        }}
        className={`flex-1 px-4 py-6 border font-semibold ${actionMetadata.button1.className}`}
      >
        {actionMetadata.button1.title}
      </Button>
      {actionMetadata.button2 && (
        <Button
          disabled={actionMetadata.button2.fetching}
          onClick={() => {
            actionMetadata.button2.action();
          }}
          className={`flex-1 px-4 py-6 border font-semibold ${actionMetadata.button2.className}`}
        >
          {actionMetadata.button2.title}
        </Button>
      )}
    </div>
  );
};
